/**
 * Form Helper Utilities
 *
 * Shared utilities for form data processing across Account, Ticket, and Contact modules.
 * These functions eliminate duplication and ensure consistent data handling.
 */

// Re-export shared utilities from data-helpers for backward compatibility
export {
  convertEmptyToNull,
  formatPhoneForApi as formatPhoneNumber,
  convertNullToEmpty as convertNullToString,
} from './data-helpers';

/**
 * Builds the assigned user and team payload from assignUsers array
 *
 * @param assignUsers - Array of assigned users/teams with assignedType property
 * @returns Object with assignedType, assignedUser, and assignedTeam arrays
 *
 * @example
 * const assignUsers = [
 *   { id: 'user1', assignedType: 'USER' },
 *   { id: 'team1', assignedType: 'TEAM' }
 * ];
 * const payload = buildAssignedPayload(assignUsers);
 * // Returns: {
 * //   assignedType: ['USER', 'TEAM'],
 * //   assignedUser: ['user1'],
 * //   assignedTeam: ['team1']
 * // }
 */
export function buildAssignedPayload(assignUsers?: any[]) {
  const assignedUserIds =
    assignUsers?.filter((u: any) => u.assignedType === 'USER').map((u: any) => u.id) ?? [];

  const assignedTeamIds =
    assignUsers?.filter((u: any) => u.assignedType === 'TEAM').map((u: any) => u.id) ?? [];

  const assignedTypes: ('USER' | 'TEAM')[] = [];
  if (assignedUserIds.length) assignedTypes.push('USER');
  if (assignedTeamIds.length) assignedTypes.push('TEAM');

  return {
    assignedType: assignedTypes,
    assignedUser: assignedUserIds,
    assignedTeam: assignedTeamIds,
  };
}

/**
 * Extracts custom fields from form data
 * Custom fields are identified by 'cf_' prefix
 *
 * @param data - Form data object
 * @returns Object containing only custom field key-value pairs
 *
 * @example
 * const formData = {
 *   name: 'John',
 *   cf_custom1: 'value1',
 *   email: 'john@example.com',
 *   cf_custom2: 'value2'
 * };
 * const customFields = extractCustomFields(formData);
 * // Returns: { cf_custom1: 'value1', cf_custom2: 'value2' }
 */
export function extractCustomFields(data: Record<string, any>) {
  const customFldKeys = Object.keys(data).filter((item) => item.toLowerCase().startsWith('cf_'));
  return customFldKeys.reduce<Record<string, unknown>>((acc, key) => {
    acc[key] = data[key];
    return acc;
  }, {});
}

/**
 * Adds audit fields (createdById or modifiedById) based on whether record is being edited
 *
 * @param isEdit - Whether this is an edit operation (true) or create operation (false)
 * @param userId - ID of the current user
 * @returns Object with either modifiedById or createdById
 *
 * @example
 * const auditFields = addAuditFields(true, 'user123');
 * // Returns: { modifiedById: 'user123' }
 *
 * const auditFields = addAuditFields(false, 'user123');
 * // Returns: { createdById: 'user123' }
 */
export function addAuditFields(isEdit: boolean, userId?: string) {
  if (!userId) return {};
  return isEdit ? { modifiedById: userId } : { createdById: userId };
}
